export interface InventoryIngredient {
  id: string;
  company_id: number;
  name: string;
  name_en: string | null;
  sku: string | null;
  unit: string;
  cost: number | null;
  category: string | null;
  low_stock_threshold: number | null;
  reorder_point: number | null;
  status: boolean;
  deleted: boolean;
  created_at: string;
  updated_at: string;
  locations: InventoryIngredientLocation[];
  movements: InventoryMovement[];
  conversions: InventoryUnitConversion[];
  total_stock: number | null;
}

export interface InventoryIngredientLocation {
  id: string;
  ingredient_id: number;
  company_id: number;
  branch_id: number;
  location: string;
  quantity: number;
  updated_at: string;
  ingredient: InventoryIngredient | null;
  branch: { id: string; name: string } | null;
}

export interface InventoryMovement {
  id: string;
  company_id: number;
  branch_id: number;
  ingredient_id: number;
  quantity_change: number;
  movement_type: string;
  reference_type: string;
  reference_id: number | null;
  location: string | null;
  cost_per_unit: number | null;
  notes: string | null;
  performed_by: number | null;
  created_at: string;
  ingredient: InventoryIngredient | null;
  branch: { id: string; name: string } | null;
}

export interface InventoryUnitConversion {
  id: string;
  company_id: number;
  ingredient_id: number;
  from_unit: string;
  to_unit: string;
  factor: number;
}

// Built-in conversions for standard unit pairs.
// Key format: "from_unit:to_unit", value = how many to_unit in 1 from_unit.
export const DEFAULT_CONVERSIONS: Record<string, number> = {
  "kg:g": 1000,
  "g:kg": 0.001,
  "l:ml": 1000,
  "ml:l": 0.001,
  "lb:oz": 16,
  "oz:lb": 0.0625,
};

export const UNIT_OPTIONS = [
  { value: "g", label: "Gramos (g)" },
  { value: "kg", label: "Kilogramos (kg)" },
  { value: "ml", label: "Mililitros (ml)" },
  { value: "l", label: "Litros (l)" },
  { value: "pz", label: "Piezas (pz)" },
  { value: "box", label: "Caja (box)" },
  { value: "portion", label: "Porción" },
  { value: "oz", label: "Onzas (oz)" },
  { value: "lb", label: "Libras (lb)" },
];

export const LOCATION_OPTIONS = [
  { value: "kitchen", label: "Cocina" },
  { value: "bar", label: "Barra" },
  { value: "freezer", label: "Congelador" },
  { value: "warehouse", label: "Almacén" },
  { value: "other", label: "Otro" },
];

export const CATEGORY_OPTIONS = [
  { value: "proteins", label: "Proteínas", labelEn: "Proteins" },
  { value: "dairy", label: "Lácteos", labelEn: "Dairy" },
  { value: "produce", label: "Frutas y Verduras", labelEn: "Produce" },
  { value: "bakery", label: "Panadería", labelEn: "Bakery" },
  { value: "dry_goods", label: "Abarrotes", labelEn: "Dry Goods" },
  { value: "beverages", label: "Bebidas", labelEn: "Beverages" },
  { value: "spices", label: "Especias", labelEn: "Spices" },
  { value: "oils", label: "Aceites", labelEn: "Oils" },
  { value: "frozen", label: "Congelados", labelEn: "Frozen" },
  { value: "packaging", label: "Empaque", labelEn: "Packaging" },
  { value: "cleaning", label: "Limpieza", labelEn: "Cleaning" },
  { value: "other", label: "Otro", labelEn: "Other" },
];

export const CATEGORY_LABELS: Record<string, { label: string; labelEn: string }> = Object.fromEntries(
  CATEGORY_OPTIONS.map((c) => [c.value, { label: c.label, labelEn: c.labelEn }])
);

export interface Recipe {
  id: string;
  company_id: number;
  product_id: number;
  size_id: number | null;
  name: string | null;
  yield_qty: number;
  notes: string | null;
  status: boolean;
  created_at: string;
  updated_at: string;
  product: {
    id: string;
    title: string;
    price: number;
    has_size: boolean;
    sizes: { id: string; name: string; price: number }[];
  } | null;
  ingredients: RecipeIngredient[];
  food_cost: number | null;
}

export interface RecipeIngredient {
  id: string;
  recipe_id: number;
  ingredient_id: number;
  quantity: number;
  unit: string;
  is_optional: boolean;
  optional_ingredient_id: number | null;
  waste_factor: number;
  notes: string | null;
  inventory_ingredient: InventoryIngredient | null;
}

export interface RecipeIngredientInput {
  ingredient_id: string;
  quantity: number;
  unit: string;
  is_optional?: boolean;
  optional_ingredient_id?: number | null;
  waste_factor?: number;
  notes?: string;
}

export interface ProductCost {
  product_id: number;
  product: {
    id: string;
    title: string;
    price: number;
    has_size: boolean;
  } | null;
  recipe_id: number | null;
  food_cost: number | null;
  price: number | null;
  margin: number | null;
  margin_pct: number | null;
}

export interface Supplier {
  id: string;
  company_id: number;
  name: string;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  status: boolean;
  created_at: string;
  updated_at: string;
  ingredients: SupplierIngredient[];
  purchase_orders: PurchaseOrder[];
}

export interface SupplierIngredient {
  id: string;
  supplier_id: number;
  ingredient_id: number;
  unit_price: number;
  unit: string;
  min_order_qty: number | null;
  lead_time_days: number | null;
  sku: string | null;
  is_preferred: boolean;
  last_updated: string | null;
  supplier: Supplier | null;
  ingredient: InventoryIngredient | null;
}

export interface PurchaseOrder {
  id: string;
  company_id: number;
  branch_id: number;
  supplier_id: number;
  status: string;
  order_date: string | null;
  expected_date: string | null;
  received_date: string | null;
  subtotal: number | null;
  tax: number | null;
  total: number | null;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  supplier: Supplier | null;
  branch: { id: string; name: string } | null;
  items: PurchaseOrderItem[];
}

export interface PurchaseOrderItem {
  id: string;
  purchase_order_id: number;
  ingredient_id: number;
  quantity_ordered: number;
  quantity_received: number | null;
  quantity_damaged: number | null;
  unit: string;
  unit_price: number;
  total: number;
  notes: string | null;
  ingredient: InventoryIngredient | null;
}

export interface SuggestedPurchase {
  ingredient: InventoryIngredient;
  current_stock: number;
  reorder_point: number;
  suggested_qty: number;
  preferred_supplier: Supplier | null;
  unit_price: number | null;
  lead_time_days: number | null;
}

export const PO_STATUS_LABELS: Record<string, { label: string; labelEn: string; color: string }> = {
  draft: { label: "Borrador", labelEn: "Draft", color: "secondary" },
  sent: { label: "Enviada", labelEn: "Sent", color: "info" },
  partial: { label: "Parcial", labelEn: "Partial", color: "warning" },
  received: { label: "Recibida", labelEn: "Received", color: "success" },
  cancelled: { label: "Cancelada", labelEn: "Cancelled", color: "danger" },
};

export const MOVEMENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  sale: { label: "Venta", color: "danger" },
  purchase: { label: "Compra", color: "success" },
  waste: { label: "Merma", color: "warning" },
  adjustment: { label: "Ajuste", color: "info" },
  transfer_in: { label: "Entrada Transfer.", color: "success" },
  transfer_out: { label: "Salida Transfer.", color: "danger" },
  production: { label: "Producción", color: "primary" },
  return: { label: "Devolución", color: "success" },
};

// ─── PHASE 5: STOCK COUNTS ──────────────────────────────────────────────────

export interface StockCount {
  id: string;
  company_id: number;
  branch_id: number;
  count_type: string;
  status: string;
  started_by: number | null;
  completed_by: number | null;
  started_at: string;
  completed_at: string | null;
  notes: string | null;
  branch: { id: string; name: string } | null;
  items: StockCountItem[];
  total_items: number;
  total_variance_cost: number | null;
}

export interface StockCountItem {
  id: string;
  stock_count_id: number;
  ingredient_id: number;
  location: string | null;
  expected_qty: number | null;
  counted_qty: number | null;
  variance: number | null;
  variance_reason: string | null;
  variance_notes: string | null;
  unit: string | null;
  cost_at_count: number | null;
  ingredient: InventoryIngredient | null;
  variance_cost: number | null;
}

export interface VarianceReport {
  total_counts: number;
  total_items: number;
  items_with_variance: number;
  total_variance_cost: number;
  by_reason: VarianceByReason[];
  by_ingredient: VarianceByIngredient[];
}

export interface VarianceByReason {
  reason: string;
  count: number;
  total_variance_cost: number;
}

export interface VarianceByIngredient {
  ingredient: InventoryIngredient | null;
  total_variance: number;
  total_variance_cost: number;
  count: number;
}

export const COUNT_TYPE_LABELS: Record<string, { label: string; labelEn: string }> = {
  full: { label: "Completo", labelEn: "Full" },
  quick: { label: "Rápido", labelEn: "Quick" },
  category: { label: "Por Categoría", labelEn: "By Category" },
};

export const COUNT_STATUS_LABELS: Record<string, { label: string; labelEn: string; color: string }> = {
  in_progress: { label: "En Progreso", labelEn: "In Progress", color: "warning" },
  completed: { label: "Completado", labelEn: "Completed", color: "success" },
  cancelled: { label: "Cancelado", labelEn: "Cancelled", color: "danger" },
};

export const VARIANCE_REASON_LABELS: Record<string, { label: string; labelEn: string }> = {
  spoilage: { label: "Merma / Caducidad", labelEn: "Spoilage" },
  theft: { label: "Robo / Faltante", labelEn: "Theft" },
  prep_loss: { label: "Pérdida en Preparación", labelEn: "Prep Loss" },
  error: { label: "Error de Conteo", labelEn: "Counting Error" },
  other: { label: "Otro", labelEn: "Other" },
};

// ─── PHASE 6: WASTE & SPOILAGE ──────────────────────────────────────────────

export interface WasteByIngredient {
  ingredient: InventoryIngredient | null;
  total_quantity: number;
  total_cost: number;
  count: number;
}

export interface WasteByReason {
  reason: string;
  count: number;
  total_cost: number;
}

export interface WasteReport {
  total_waste_cost: number;
  total_entries: number;
  by_ingredient: WasteByIngredient[];
  by_reason: WasteByReason[];
}

export const WASTE_REASON_LABELS: Record<string, { label: string; labelEn: string; color: string }> = {
  expired: { label: "Caducado", labelEn: "Expired", color: "danger" },
  burned: { label: "Quemado", labelEn: "Burned", color: "warning" },
  prep_leftover: { label: "Sobrante de Preparación", labelEn: "Prep Leftover", color: "info" },
  returned_plate: { label: "Plato Devuelto", labelEn: "Returned Plate", color: "secondary" },
  damaged: { label: "Dañado", labelEn: "Damaged", color: "dark" },
  other: { label: "Otro", labelEn: "Other", color: "light" },
};

// ─── PHASE 7: PRODUCTION ────────────────────────────────────────────────────

export interface ProductionRecipe {
  id: string;
  company_id: number;
  name: string;
  output_ingredient_id: number;
  output_quantity: number;
  output_unit: string;
  notes: string | null;
  status: boolean;
  created_at: string | null;
  updated_at: string | null;
  output_ingredient: InventoryIngredient | null;
  inputs: ProductionRecipeInputType[];
  batches: ProductionBatch[];
}

export interface ProductionRecipeInputType {
  id: string;
  production_recipe_id: number;
  ingredient_id: number;
  quantity: number;
  unit: string;
  ingredient: InventoryIngredient | null;
}

export interface ProductionBatch {
  id: string;
  company_id: number;
  branch_id: number;
  production_recipe_id: number;
  actual_output: number | null;
  status: string;
  performed_by: number | null;
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
  production_recipe: ProductionRecipe | null;
  branch: { id: string; name: string } | null;
}

export const PRODUCTION_BATCH_STATUS_LABELS: Record<string, { label: string; labelEn: string; color: string }> = {
  in_progress: { label: "En Progreso", labelEn: "In Progress", color: "warning" },
  completed: { label: "Completado", labelEn: "Completed", color: "success" },
  cancelled: { label: "Cancelado", labelEn: "Cancelled", color: "danger" },
};

// ─── PHASE 8: COSTING & MARGINS ────────────────────────────────────────────

export interface MenuProfitabilityItem {
  product_id: number;
  product: { id: string; title: string; price: number } | null;
  price: number;
  food_cost: number | null;
  margin: number | null;
  margin_pct: number | null;
  has_recipe: boolean;
}

export interface MenuProfitabilityReport {
  total_items: number;
  items_with_recipe: number;
  avg_margin_pct: number | null;
  items: MenuProfitabilityItem[];
}

export interface CostDriverItem {
  ingredient: InventoryIngredient | null;
  total_quantity: number;
  total_cost: number;
  pct_of_total: number;
}

export interface CostDriversReport {
  total_cost: number;
  startDate: string;
  endDate: string;
  items: CostDriverItem[];
}

export interface FoodCostReport {
  total_ingredient_cost: number;
  total_revenue: number;
  food_cost_pct: number;
  startDate: string;
  endDate: string;
}

export interface TheoreticalVsActualItem {
  ingredient: InventoryIngredient | null;
  theoretical_qty: number;
  actual_qty: number;
  variance: number;
  variance_pct: number | null;
  unit: string | null;
}

export interface TheoreticalVsActualReport {
  startDate: string;
  endDate: string;
  items: TheoreticalVsActualItem[];
}

// ─── PHASE 10: REPORTS DASHBOARD ──────────────────────────────────────────

export interface InventoryValuationItem {
  ingredient: InventoryIngredient | null;
  total_stock: number;
  cost_per_unit: number;
  total_value: number;
}

export interface InventoryValuation {
  total_value: number;
  total_items: number;
  items: InventoryValuationItem[];
}
